import differenceInDays from 'date-fns/esm/differenceInDays';
import isBefore from 'date-fns/esm/isBefore';
import isWithinInterval from 'date-fns/esm/isWithinInterval';
import startOfDay from 'date-fns/esm/startOfDay';
import orderBy from 'lodash-es/orderBy';
import { createSelector } from 'reselect';
import { IState } from '..';
import { IParsedUnallocatedIdentifier } from '../../logistics/unallocatedProductSummary/hooks/useParsedParameter';
import { getLogisticsUserWarehouses } from '../auth';
import { ILogisticsThreshold } from '../lookups';
import {
  IDealerUnallocatedProduct,
  IDealerUnallocatedProductHash,
  ILogisticsSidebarFilters
} from './unallocatedProductsInterfaces';

const getUnallocatedProductArray = createSelector(
  (state: IState) => state.unallocatedProducts.lines,
  lineHash => Object.values(lineHash)
);

const getUserFilteredUnallocatedProducts = createSelector(
  [getUnallocatedProductArray, getLogisticsUserWarehouses],
  (lines, warehouses) =>
    lines.filter(x => warehouses.includes(x.originWarehouseId) || warehouses.includes(x.destinationWarehouseId))
);

export const getUnallocatedProductByDealerIdentifier = createSelector(
  [getUserFilteredUnallocatedProducts, (_: any, identifier: IParsedUnallocatedIdentifier) => identifier],
  (arr, identifier) => {
    return arr.filter(
      x =>
        // replacing all non-alphanumeric characters for comparrison to accomidate for new line characters
        identifier.address.replace(/\W/g, '') === x.address.replace(/\W/g, '') &&
        identifier.city.replace(/\W/g, '') === x.city.replace(/\W/g, '') &&
        identifier.custAccount === x.custAccount &&
        identifier.deliveryMode === x.deliveryMode &&
        identifier.state === x.state &&
        identifier.unallocatedProductTypeId === x.unallocatedProductTypeId &&
        identifier.warehouseId === x.originWarehouseId &&
        identifier.zip.replace(/\W/g, '') === x.zip.replace(/\W/g, '')
    );
  }
);

export const getUnallocatedProducts = createSelector(
  getUserFilteredUnallocatedProducts,
  (_: unknown, filters: ILogisticsSidebarFilters) => filters,
  (unallocated, filters): IDealerUnallocatedProduct[] => {
    filters.search = filters.search.toLowerCase();

    const obj = unallocated.reduce<Record<string, IDealerUnallocatedProductHash>>((hash, line) => {
      const shouldProcess =
        (!filters.search ||
          line.custAccount.includes(filters.search) ||
          line.name.toLowerCase().includes(filters.search) ||
          line.city.toLowerCase().includes(filters.search)) &&
        (!filters.state || line.state === filters.state) &&
        (!filters.type || line.unallocatedProductTypeId === filters.type) &&
        (!filters.companyId || line.companyId === filters.companyId);

      if (shouldProcess) {
        // tslint:disable-next-line: max-line-length
        const id = encodeURIComponent(
          `${line.unallocatedProductTypeId}_${line.custAccount}_${line.deliveryMode}_${line.address}_${line.city}_${line.state}_${line.zip}_${line.originWarehouseId}`
        );
        const date = new Date(line.shippingDateRequested);
        const dateId = date.toDateString();

        if (hash[id]) {
          if (isBefore(date, hash[id].minDate)) {
            hash[id].minDate = date;
          }
          if (hash[id].dates[dateId]) {
            hash[id].dates[dateId].lines.push(line);
          } else {
            hash[id].dates = {
              ...hash[id].dates,
              [dateId]: { date, lines: [line], id: `${hash[id].id}_${dateId}`, shipMethod: line.deliveryMode }
            };
          }
        } else {
          hash[id] = {
            accountNum: line.custAccount,
            dates: { [dateId]: { date, lines: [line], id: `${id}_${dateId}`, shipMethod: line.deliveryMode } },
            destinationWarehouseId: line.destinationWarehouseId,
            id,
            location: `${line.city}, ${line.state}`,
            minDate: date,
            name: line.name,
            originWarehouseId: line.originWarehouseId,
            shipMethod: line.deliveryMode,
            state: line.state
          };
        }
      }
      return hash;
    }, {});

    const values = orderBy(
      Object.values(obj).map<IDealerUnallocatedProduct>(x => ({ ...x, dates: Object.values(x.dates) })),
      x => (filters.sort === 'shippingDateRequested' ? x.minDate : x[filters.sort])
    );

    return values;
  }
);

export const getLineThreshold = createSelector(
  [(state: IState) => state.lookups.logitsicsThresholds.items, (_: IState, date: Date) => date],
  (thresholdArr, date): ILogisticsThreshold => {
    const diff = differenceInDays(date, new Date());

    const thresholds = thresholdArr.filter(x => x.daysFromToday >= diff);

    return orderBy(thresholds, x => x.daysFromToday)[0];
  }
);

export const getUnallocatedProductDailyTotals = createSelector(
  [
    getUserFilteredUnallocatedProducts,
    (state: IState) => state.logistics.dashboardFilters,
    (_: IState, range: Interval) => range
  ],
  (lines, filters, range): Record<number, number> =>
    lines.reduce<Record<number, number>>((hash, unallocatedProduct) => {
      const key = startOfDay(new Date(unallocatedProduct.shippingDateRequested)).getTime();
      let originalHash = hash[key] ?? 0;
      if (
        isWithinInterval(new Date(unallocatedProduct.shippingDateRequested), range) &&
        (filters.warehouses.length === 0 ||
          filters.warehouses.includes(unallocatedProduct.originWarehouseId) ||
          filters.warehouses.includes(unallocatedProduct.destinationWarehouseId)) &&
        (!filters.company || filters.company === unallocatedProduct.companyId)
      ) {
        hash[key] = originalHash += unallocatedProduct.lineAmount;
      }
      return hash;
    }, {})
);

export const getRangeLineAmount = (range: { startDate: Date | number; endDate: Date | number }) =>
  createSelector(getUserFilteredUnallocatedProducts, lines =>
    lines
      .filter(o =>
        isWithinInterval(new Date(o.shippingDateRequested), {
          end: range.endDate,
          start: range.startDate
        })
      )
      .reduce((sum, cost) => (sum += cost.lineAmount), 0)
  );

export const getUallocatedAmounts = createSelector(
  [getUserFilteredUnallocatedProducts, (state: IState) => state.logistics.dashboardFilters],
  (lines, filters) => {
    const now = new Date();
    return lines.reduce(
      (totals, item) => {
        if (
          (!filters.company || filters.company === item.companyId) &&
          (filters.warehouses.length === 0 ||
            filters.warehouses.includes(item.originWarehouseId) ||
            filters.warehouses.includes(item.destinationWarehouseId))
        ) {
          totals.total += item.lineAmount;

          if (isBefore(new Date(item.shippingDateRequested), now)) {
            totals.pastDue += item.lineAmount;
          }
        }
        return totals;
      },
      { total: 0, pastDue: 0 }
    );
  }
);
